import {
  useQuery,
  useMutation,
  QueryFunction,
  QueryKey,
  UseQueryOptions,
  UseMutationOptions,
} from 'react-query'
import { AxiosError } from 'axios'

import api from 'api'
import { EstateAgentBasicInfoDTO } from 'types'

import {
  CountAgentsPayload,
  SearchAgentsResponse,
  AdminAgentFilterOptions,
  EstateAgentUserDTO,
  EstateAgentUpdateStatusDTO,
} from './types'

const TIME = 1000 * 60 * 1 // 1 min

const SERVICE_NAME = `Agent`

const CONTROLLER_PATH = '/app/spourgiti/admin/estate-agent'

const QueryKeys = {
  count: `${SERVICE_NAME}#count`,
  all: `${SERVICE_NAME}#all`,
  single: `${SERVICE_NAME}#single`,
  basicInfo: `${SERVICE_NAME}#basicInfo`,
  estateAgents: `${SERVICE_NAME}#estateAgents`,
} as const

const MutationKeys = {
  create: `${SERVICE_NAME}#create`,
  update: `${SERVICE_NAME}#update`,
  statusUpdate: `${SERVICE_NAME}#status`,
  delete: `${SERVICE_NAME}#delete`,
} as const

// COUNT all
export const getCountAgents = async ({ queryKey }): Promise<number> => {
  const [, filterOptions] = queryKey

  const { data } = await api({
    method: 'post',
    url: `${CONTROLLER_PATH}/count-by-criteria`,
    data: filterOptions as CountAgentsPayload,
  })
  return data
}

export const useCountAgents = (
  filterOptions: AdminAgentFilterOptions = {},
  queryOptions: UseQueryOptions = {}
) => {
  return useQuery([QueryKeys.count, filterOptions], getCountAgents, {
    keepPreviousData: true,
    staleTime: TIME,
    cacheTime: TIME,
    ...queryOptions,
  })
}

// GET all
export const getAgents: QueryFunction<SearchAgentsResponse, QueryKey> = async ({
  queryKey,
}): Promise<SearchAgentsResponse> => {
  const [
    ,
    page,
    pageSize,
    filterOptions = {} as AdminAgentFilterOptions,
    sortOptions = {} as AdminAgentFilterOptions,
  ] = queryKey

  const { data } = await api({
    method: 'post',
    url: `${CONTROLLER_PATH}/basic-info/search-by-criteria`,
    data: {
      pageNumber: page,
      pageSize,
      ...(filterOptions as object),
      ...(sortOptions as object),
    },
  })

  return data
}
export const useAgents = (
  page: number,
  pageSize: number,
  filters: AdminAgentFilterOptions = {},
  sortOptions: AdminAgentFilterOptions = {},
  queryOptions = {}
) => {
  return useQuery(
    [QueryKeys.all, page, pageSize, filters, sortOptions],
    getAgents,
    {
      keepPreviousData: true,
      staleTime: TIME,
      cacheTime: 0,
      ...queryOptions,
    }
  )
}

// GET single
export const getAgent = async (
  id?: number | string
): Promise<EstateAgentUserDTO> => {
  const { data } = await api({
    method: 'get',
    url: `${CONTROLLER_PATH}/${id}`,
  })
  return data
}
export const useEstateAgent = <T = EstateAgentUserDTO>(
  id: string | number | undefined,
  options: UseQueryOptions<T, AxiosError, EstateAgentUserDTO> = {}
) => {
  return useQuery([QueryKeys.single, id], () => getAgent(id), {
    staleTime: id ? TIME : 0,
    cacheTime: id ? TIME : 0,
    ...options,
  } as Omit<UseQueryOptions<EstateAgentUserDTO, AxiosError, EstateAgentUserDTO, QueryKey>, 'queryKey' | 'queryFn'>)
}

// GET single basic info
export const getAgentBasicInfo = async (
  id?: number | string
): Promise<EstateAgentBasicInfoDTO> => {
  const { data } = await api({
    method: 'get',
    url: `${CONTROLLER_PATH}/basic-info/${id}`,
  })
  return data
}
export const getEstateAgents = async () => {
  const { data } = await api({
    method: 'get',
    url: `${CONTROLLER_PATH}-system/get-all `,
  })
  return data
}

export const useEstateAgents = (queryOptions = {}) => {
  return useQuery([QueryKeys.estateAgents], getEstateAgents, {
    ...queryOptions,
  })
}

export const useAgentBasicInfo = <T = EstateAgentBasicInfoDTO>(
  id: string | number | undefined,
  options: UseQueryOptions<T, AxiosError, EstateAgentBasicInfoDTO> = {}
) => {
  return useQuery([QueryKeys.basicInfo, id], () => getAgentBasicInfo(id), {
    staleTime: id ? TIME : 0,
    cacheTime: 0,
    ...options,
  } as Omit<UseQueryOptions<EstateAgentBasicInfoDTO, AxiosError, EstateAgentBasicInfoDTO, QueryKey>, 'queryKey' | 'queryFn'>)
}

// create
export const createAgent = async (
  payload: EstateAgentUserDTO
): Promise<EstateAgentUserDTO> => {
  const { data } = await api({
    method: 'put',
    url: `${CONTROLLER_PATH}/`,
    data: {
      ...payload,
    },
  })
  return data
}

export const useCreateAgent = (
  options: Omit<
    UseMutationOptions<EstateAgentUserDTO, AxiosError, EstateAgentUserDTO>,
    'mutationKey' | 'mutationFn'
  > = {}
) => {
  return useMutation(MutationKeys.create, createAgent, {
    ...options,
  } as Omit<UseMutationOptions<EstateAgentUserDTO, AxiosError, EstateAgentUserDTO>, 'mutationKey' | 'mutationFn'>)
}
// update
export const updateAgent = async ({ id, payload }) => {
  const { data } = await api({
    method: 'put',
    url: `${CONTROLLER_PATH}/`,
    data: {
      ...payload,
    },
  })
  return data
}

export const useUpdateAgent = (
  options: Omit<
    UseMutationOptions<
      EstateAgentUserDTO,
      AxiosError,
      { id: string; payload: EstateAgentUserDTO }
    >,
    'mutationKey' | 'mutationFn'
  > = {}
) => {
  return useMutation(MutationKeys.update, updateAgent, {
    ...options,
  } as Omit<UseMutationOptions<EstateAgentUserDTO, AxiosError, { id: string; payload: EstateAgentUserDTO }>, 'mutationKey' | 'mutationFn'>)
}

//UPDATE STATUS
// update
export const updateAgentStatus = async (
  estateAgentStatus: EstateAgentUpdateStatusDTO
) => {
  const { data } = await api({
    method: 'put',
    url: `${CONTROLLER_PATH}/${estateAgentStatus.id}/status`,
    data: {
      status: estateAgentStatus.status,
    },
  })
  return data
}

export const useUpdateAgentStatus = (
  options: Omit<
    UseMutationOptions<
      EstateAgentUserDTO,
      AxiosError,
      EstateAgentUpdateStatusDTO
    >,
    'mutationKey' | 'mutationFn'
  > = {}
) => {
  return useMutation(MutationKeys.statusUpdate, updateAgentStatus, {
    ...options,
  } as Omit<UseMutationOptions<EstateAgentUserDTO, AxiosError, EstateAgentUpdateStatusDTO>, 'mutationKey' | 'mutationFn'>)
}

// DELETE
export const deleteAgent = async (id: number) => {
  const { data } = await api({
    method: 'delete',
    url: `${CONTROLLER_PATH}/${id}`,
  })
  return data
}

export const useDeleteAgent = (
  options: Omit<
    UseMutationOptions<number, AxiosError, number>,
    'mutationKey' | 'mutationFn'
  > = {}
) => {
  return useMutation(MutationKeys.delete, deleteAgent, {
    ...options,
  } as Omit<UseMutationOptions<number, AxiosError, number>, 'mutationKey' | 'mutationFn'>)
}

const AgentService = {
  deleteAgent,
  updateAgentStatus,
  updateAgent,
  createAgent,
  getAgentBasicInfo,
  getAgent,
  getAgents,
  getCountAgents,
  getEstateAgents,
  hooks: {
    useDeleteAgent,
    useUpdateAgentStatus,
    useUpdateAgent,
    useCreateAgent,
    useAgentBasicInfo,
    useEstateAgent,
    useAgents,
    useCountAgents,
    useEstateAgents,
  },
  keys: { QueryKeys, MutationKeys },
}

export default AgentService
